/**
 * 纹理图集打包器
 * 将多个碎片纹理打包到一张或多张大图中，优化 GPU 渲染性能
 */

export interface AtlasFrame {
    x: number;
    y: number;
    width: number;
    height: number;
    atlasIndex: number;  // 所在图集的索引
}

export interface PackedPiece {
    id: string;
    frame: AtlasFrame;
    originalCanvas: HTMLCanvasElement;
}

export interface AtlasConfig {
    maxSize: number;      // 图集最大尺寸，默认 2048
    padding: number;      // 碎片之间的间距，默认 2
    allowRotation: boolean; // 是否允许旋转以节省空间，默认 false
}

/**
 * 矩形节点，用于 bin-packing 算法
 */
interface PackNode {
    x: number;
    y: number;
    width: number;
    height: number;
    used: boolean;
    right?: PackNode;
    down?: PackNode;
}

export class TextureAtlasPacker {
    private config: AtlasConfig;
    private atlases: HTMLCanvasElement[] = [];
    private contexts: CanvasRenderingContext2D[] = [];
    private frames: Map<string, AtlasFrame> = new Map();
    private roots: PackNode[] = [];

    constructor(config?: Partial<AtlasConfig>) {
        this.config = {
            maxSize: 2048,
            padding: 2,
            allowRotation: false,
            ...config
        };
    }

    /**
     * 打包所有碎片纹理到图集
     * @param pieces 碎片数组，每个包含 id 和 texture (Canvas)
     */
    packAll(pieces: { id: string; texture: HTMLCanvasElement }[]): void {
        // 按面积从大到小排序，提高打包效率
        const sortedPieces = [...pieces].sort((a, b) => {
            const areaA = a.texture.width * a.texture.height;
            const areaB = b.texture.width * b.texture.height;
            return areaB - areaA;
        });

        // 创建第一张图集
        this.createNewAtlas();

        for (const piece of sortedPieces) {
            this.packPiece(piece.id, piece.texture);
        }
    }

    /**
     * 打包单个碎片
     */
    private packPiece(id: string, texture: HTMLCanvasElement): boolean {
        const width = texture.width + this.config.padding;
        const height = texture.height + this.config.padding;

        // 尝试在现有图集中找到位置
        for (let i = 0; i < this.atlases.length; i++) {
            const node = this.findNode(this.roots[i], width, height);
            if (node) {
                const splitNode = this.splitNode(node, width, height);
                this.drawPieceToAtlas(i, texture, splitNode.x, splitNode.y);
                this.frames.set(id, {
                    x: splitNode.x,
                    y: splitNode.y,
                    width: texture.width,
                    height: texture.height,
                    atlasIndex: i
                });
                return true;
            }
        }

        // 现有图集放不下，创建新图集
        const newIndex = this.createNewAtlas();
        const node = this.findNode(this.roots[newIndex], width, height);
        if (node) {
            const splitNode = this.splitNode(node, width, height);
            this.drawPieceToAtlas(newIndex, texture, splitNode.x, splitNode.y);
            this.frames.set(id, {
                x: splitNode.x,
                y: splitNode.y,
                width: texture.width,
                height: texture.height,
                atlasIndex: newIndex
            });
            return true;
        }

        console.warn(`Piece ${id} is too large to fit in atlas`);
        return false;
    }

    /**
     * 创建新的图集 Canvas
     */
    private createNewAtlas(): number {
        const canvas = document.createElement('canvas');
        canvas.width = this.config.maxSize;
        canvas.height = this.config.maxSize;
        const ctx = canvas.getContext('2d')!;

        this.atlases.push(canvas);
        this.contexts.push(ctx);

        // 创建根节点
        this.roots.push({
            x: 0,
            y: 0,
            width: this.config.maxSize,
            height: this.config.maxSize,
            used: false
        });

        return this.atlases.length - 1;
    }

    /**
     * 在节点树中查找可用空间（bin-packing 核心算法）
     */
    private findNode(root: PackNode, width: number, height: number): PackNode | null {
        if (root.used) {
            // 已使用，递归查找子节点
            const rightNode = root.right ? this.findNode(root.right, width, height) : null;
            if (rightNode) return rightNode;
            return root.down ? this.findNode(root.down, width, height) : null;
        } else if (width <= root.width && height <= root.height) {
            // 找到合适的空间
            return root;
        }
        return null;
    }

    /**
     * 分割节点（放置碎片后，将剩余空间分成两个子节点）
     */
    private splitNode(node: PackNode, width: number, height: number): PackNode {
        node.used = true;

        // 向下的剩余空间
        node.down = {
            x: node.x,
            y: node.y + height,
            width: node.width,
            height: node.height - height,
            used: false
        };

        // 向右的剩余空间
        node.right = {
            x: node.x + width,
            y: node.y,
            width: node.width - width,
            height: height,
            used: false
        };

        return node;
    }

    /**
     * 将碎片绘制到图集
     */
    private drawPieceToAtlas(
        atlasIndex: number,
        texture: HTMLCanvasElement,
        x: number,
        y: number
    ): void {
        const ctx = this.contexts[atlasIndex];
        ctx.drawImage(texture, x, y);
    }

    /**
     * 获取碎片在图集中的位置
     */
    getFrame(id: string): AtlasFrame | undefined {
        return this.frames.get(id);
    }

    /**
     * 获取所有图集 Canvas
     */
    getAtlases(): HTMLCanvasElement[] {
        return this.atlases;
    }

    /**
     * 获取指定图集 Canvas
     */
    getAtlas(index: number): HTMLCanvasElement | undefined {
        return this.atlases[index];
    }

    /**
     * 获取图集数量
     */
    getAtlasCount(): number {
        return this.atlases.length;
    }

    /**
     * 获取所有帧数据
     */
    getAllFrames(): Map<string, AtlasFrame> {
        return this.frames;
    }

    /**
     * 计算图集利用率
     */
    getUtilization(): number[] {
        return this.atlases.map((atlas, index) => {
            let usedArea = 0;
            this.frames.forEach(frame => {
                if (frame.atlasIndex === index) {
                    usedArea += frame.width * frame.height;
                }
            });
            const totalArea = atlas.width * atlas.height;
            return usedArea / totalArea;
        });
    }

    /**
     * 清空所有数据
     */
    clear(): void {
        this.atlases = [];
        this.contexts = [];
        this.frames.clear();
        this.roots = [];
    }

    /**
     * 导出图集为 base64（用于调试）
     */
    exportAsBase64(index: number): string | null {
        const atlas = this.atlases[index];
        if (!atlas) return null;
        return atlas.toDataURL('image/png');
    }
}
